import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useUserAuth } from "@/hooks/use-user-auth";
import { useIsDesktop, useKeyboardState } from "@/hooks/use-viewport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, MessageSquare, Minus, Send, X } from "lucide-react";

type Topic = { id: number; title: string };

type WidgetChatRow = {
  id: number;
  topicTitle: string;
  status: string;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  unreadCount?: number;
  source?: "user" | "guest";
};

type Attachment = { url: string; mime?: string | null; bytes?: number | null; originalName?: string | null };

type WidgetMessage = {
  id: number;
  chatId: number;
  senderType: "user" | "admin" | "system" | string;
  text?: string | null;
  attachments?: Attachment[];
  createdAt?: string | null;
  sender?: { id: string; name: string; profileImageUrl?: string | null } | null;
  _clientStatus?: "pending" | "failed";
};

type WidgetMessagesResponse = {
  chat: {
    id: number;
    topicTitle: string;
    status: string;
    assignedAgent?: { id: string; name: string; profileImageUrl?: string | null } | null;
    userLastReadMessageId: number;
    adminLastReadMessageId: number;
  };
  items: WidgetMessage[];
};

function fmtTime(iso: string | null | undefined) {
  if (!iso) return "";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString();
}

function isNearBottom(el: HTMLElement, thresholdPx = 80) {
  const delta = el.scrollHeight - (el.scrollTop + el.clientHeight);
  return delta <= thresholdPx;
}

function initialsFromName(name: string) {
  const s = String(name || "").trim();
  if (!s) return "S";
  const parts = s
    .replace(/[^a-zA-Z0-9\s@._-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const letters = parts
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return letters || "S";
}

/**
 * Height of the sticky site navbar band at the top of the visible area.
 *
 * The navbar is `sticky top-0 z-[100]` (Layout.tsx) while this widget sits at
 * `z-[60]`, so any panel tall enough to reach that band slides its own header —
 * and the minimize button in it — underneath the navbar, leaving a half-covered
 * title row. Measuring beats assuming: the navbar's height differs across
 * breakpoints, and subtracting `visualViewport.offsetTop` returns 0 once the
 * sticky band has been scrolled out of the visible strip, so no space is
 * reserved for something that is not on screen.
 */
function navBandHeight(viewportOffsetTop: number) {
  try {
    const header = document.querySelector("header");
    if (!header) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom - viewportOffsetTop));
  } catch {
    return 0;
  }
}

function getOrCreateDeviceId() {
  try {
    const key = "fx_support_device_id";
    const existing = String(localStorage.getItem(key) || "").trim();
    if (/^sd_[a-zA-Z0-9_-]{16,80}$/.test(existing)) return existing;
    const gen = `sd_${(crypto as any)?.randomUUID ? (crypto as any).randomUUID().replace(/-/g, "") : String(Math.random()).slice(2) + String(Date.now())}`;
    localStorage.setItem(key, gen);
    return gen;
  } catch {
    return `sd_${String(Math.random()).slice(2)}${String(Date.now())}`;
  }
}

export function SupportChatWidget() {
  const qc = useQueryClient();
  const { user } = useUserAuth();
  const [location, setLocation] = useLocation();

  // Avoid duplicating the dashboard messenger page.
  const hidden = location.startsWith("/dashboard/messages");

  const isDesktop = useIsDesktop();
  const viewport = useKeyboardState();

  const deviceId = useMemo(() => getOrCreateDeviceId(), []);
  const headers = useMemo(() => ({ "x-support-device-id": deviceId }), [deviceId]);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"home" | "new" | "chat">("home");
  const [activeChatId, setActiveChatId] = useState<number | null>(null);

  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [topicId, setTopicId] = useState<string>("");
  const [topicTitle, setTopicTitle] = useState("");
  const [firstMessage, setFirstMessage] = useState("");

  const [draft, setDraft] = useState("");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const lastMarkedReadRef = useRef<number>(0);
  /** The transcript's inner box — observed so late layout changes can be followed. */
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  /** Follow new messages while the user is sitting at the bottom of the transcript. */
  const stickRef = useRef(true);
  /** Force one snap to the bottom: opening a chat, switching chat, or sending. */
  const snapRef = useRef(true);

  const soundAllowedRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const unreadInitRef = useRef(false);
  const lastUnreadTotalRef = useRef(0);
  const inboundInitRef = useRef(false);
  const lastInboundAdminMessageIdRef = useRef(0);

  const allowSound = () => {
    soundAllowedRef.current = true;
    try {
      if (!audioCtxRef.current) {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      void audioCtxRef.current?.resume?.();
    } catch {
      // ignore
    }
  };

  const playBeep = () => {
    if (!soundAllowedRef.current) return;
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === "suspended") void ctx.resume();

      const now = ctx.currentTime;
      const mk = (freq: number, start: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.22, start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.20);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.21);
      };

      // Double "tawk-like" chirp
      mk(920, now);
      mk(660, now + 0.14);
    } catch {
      // ignore
    }
  };

  // Prefill guest identity
  useEffect(() => {
    if (user) return;
    try {
      const raw = localStorage.getItem("fx_support_guest_profile");
      const obj = raw ? JSON.parse(raw) : null;
      if (obj && typeof obj === "object") {
        setGuestName(String(obj.name || ""));
        setGuestEmail(String(obj.email || ""));
        setGuestPhone(String(obj.phone || ""));
      }
    } catch {
      // ignore
    }
  }, [user]);

  // Claim device chats after login (one-time per user+device)
  useEffect(() => {
    if (!user) return;
    try {
      const key = `fx_support_claimed:${String((user as any).id || user.email || "user")}:${deviceId}`;
      if (localStorage.getItem(key) === "1") return;
      fetch("/api/support/widget/claim", { method: "POST", credentials: "include", headers }).finally(() => {
        try {
          localStorage.setItem(key, "1");
        } catch {
          // ignore
        }
      });
    } catch {
      // ignore
    }
  }, [user, deviceId, headers]);

  const topicsQuery = useQuery<Topic[]>({
    queryKey: ["/api/support/widget/topics"],
    queryFn: async () => {
      const res = await fetch("/api/support/widget/topics", { credentials: "include" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed to load topics");
      return payload as Topic[];
    },
    staleTime: 60_000,
    retry: false,
  });

  /**
   * Options for the topic picker, always an array.
   *
   * This guard is the fix for "the dropdown doesn't open". An **empty**
   * `<SelectContent>` is not an empty dropdown — Radix renders a listbox with
   * zero items, which collapses to a ~10 px sliver and is drawn over whatever
   * is behind it. From the outside that reads as a broken control, and it looked
   * exactly like a rendering fault when the real cause was data: the topics
   * endpoint returns nothing when `support_chat_topics` has no active rows, and
   * it answers `503 MIGRATION_REQUIRED` (silently swallowed by this query) when
   * the table is missing entirely.
   */
  const topicOptions = Array.isArray(topicsQuery.data) ? topicsQuery.data : [];

  const chatsQuery = useQuery<{ deviceId: string; items: WidgetChatRow[] }>({
    queryKey: ["/api/support/widget/chats", deviceId],
    queryFn: async () => {
      const res = await fetch("/api/support/widget/chats", { credentials: "include", headers });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed to load chats");
      return payload as any;
    },
    staleTime: 8_000,
    refetchInterval: open ? 8_000 : 10_000,
    retry: false,
    enabled: !hidden,
  });

  const chats = chatsQuery.data?.items || [];
  const unreadTotal = useMemo(() => chats.reduce((sum, c) => sum + Number(c.unreadCount || 0), 0), [chats]);

  const openInMessages = async () => {
    const cid = Number(activeChatId || 0);
    const url = cid ? `/dashboard/messages?chatId=${encodeURIComponent(String(cid))}` : "/dashboard/messages";

    // Ensure any guest chats for this device are claimed before opening user inbox.
    if (user) {
      try {
        await fetch("/api/support/widget/claim", { method: "POST", credentials: "include", headers });
      } catch {
        // ignore
      }
    }

    setOpen(false);
    setView("home");
    setLocation(url);
  };

  useEffect(() => {
    // Beep when unread increases (admin replied) after initial load.
    if (!unreadInitRef.current) {
      unreadInitRef.current = true;
      lastUnreadTotalRef.current = unreadTotal;
      return;
    }
    const prev = lastUnreadTotalRef.current;
    lastUnreadTotalRef.current = unreadTotal;
    if (unreadTotal > prev) playBeep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadTotal]);

  useEffect(() => {
    if (!open) return;
    if (view !== "chat") return;
    if (activeChatId) return;
    if (!chats.length) {
      setView("home");
      return;
    }
    setActiveChatId(chats[0].id);
  }, [open, view, activeChatId, chats.length]);

  // Remember last active chat so user inbox can open it.
  useEffect(() => {
    const cid = Number(activeChatId || 0);
    if (!Number.isFinite(cid) || cid <= 0) return;
    try {
      localStorage.setItem("fx_support_last_chat_id", String(cid));
    } catch {
      // ignore
    }
  }, [activeChatId]);

  const messagesKey = (chatId: number) => ["/api/support/widget/chats", deviceId, String(chatId), "messages"] as const;

  const messagesQuery = useQuery<WidgetMessagesResponse>({
    queryKey: messagesKey(activeChatId || 0),
    enabled: open && view === "chat" && !!activeChatId && !hidden,
    queryFn: async () => {
      const res = await fetch(`/api/support/widget/chats/${activeChatId}/messages`, { credentials: "include", headers });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed to load messages");
      return payload as WidgetMessagesResponse;
    },
    staleTime: 2_000,
    refetchInterval: 6_000,
    retry: false,
  });

  const messages = messagesQuery.data?.items || [];
  const chatMeta = messagesQuery.data?.chat || null;
  const assignedAgent = chatMeta?.assignedAgent || null;

  useEffect(() => {
    // Beep when a new admin message arrives in the active chat.
    if (!open || view !== "chat") return;
    const lastAdminId = (() => {
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m && m.senderType === "admin") return Number(m.id) || 0;
      }
      return 0;
    })();

    if (!inboundInitRef.current) {
      inboundInitRef.current = true;
      lastInboundAdminMessageIdRef.current = lastAdminId;
      return;
    }

    const prev = lastInboundAdminMessageIdRef.current;
    lastInboundAdminMessageIdRef.current = lastAdminId;
    if (lastAdminId > prev) playBeep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, open, view, activeChatId]);

  const markRead = useMutation({
    mutationFn: async (lastReadMessageId: number) => {
      if (!activeChatId) return;
      const res = await fetch(`/api/support/widget/chats/${activeChatId}/read`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ lastReadMessageId }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed");
      return payload;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["/api/support/widget/chats", deviceId] });
    },
  });

  const maybeMarkRead = async () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (!isNearBottom(el)) return;
    const lastId = Number(messages[messages.length - 1]?.id || 0);
    if (!lastId) return;
    if (lastId <= lastMarkedReadRef.current) return;
    lastMarkedReadRef.current = lastId;
    markRead.mutate(lastId);
  };

  /**
   * Opening a chat — or coming back to one — means "show me the newest message".
   * Without this the transcript simply mounted at scrollTop 0, so any chat with
   * history opened at its very first message instead of its last.
   */
  useEffect(() => {
    if (!open) return;
    if (view !== "chat") return;
    stickRef.current = true;
    snapRef.current = true;
  }, [open, view, activeChatId]);

  useEffect(() => {
    if (!open || view !== "chat") return;
    if (!messages.length) return;

    // Sending, opening or switching chats always snaps to the newest message. A
    // message that arrives while the user is scrolled up reading history must NOT
    // yank them back down — that is what `stickRef` is asked about here.
    if (snapRef.current || stickRef.current) {
      snapRef.current = false;
      stickRef.current = true;
      const el = scrollerRef.current;
      if (el) {
        // Two frames: the first lets React commit the new list, the second runs after
        // the browser has laid it out, so `scrollHeight` is the final value.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
          });
        });
      }
    }

    void maybeMarkRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, open, view, activeChatId]);

  /**
   * Two boxes change size after a chat is on screen, and both can leave the newest
   * message out of view: the transcript grows (a line wraps at the panel's width, a
   * font finishes loading, a reply arrives), and the scroller shrinks (the panel's
   * max height drops as the keyboard opens, which reveals more scroll range under a
   * scrollTop that has not moved). While the user is at the bottom, both are followed.
   */
  useEffect(() => {
    if (!open || view !== "chat") return;
    const el = scrollerRef.current;
    const content = transcriptRef.current;
    if (!el || !content) return;
    if (typeof ResizeObserver === "undefined") return;

    const follow = () => {
      if (!stickRef.current && !snapRef.current) return;
      snapRef.current = false;
      el.scrollTop = el.scrollHeight;
    };

    const ro = new ResizeObserver(follow);
    ro.observe(content);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, view, activeChatId]);

  const onTranscriptScroll = () => {
    const el = scrollerRef.current;
    if (el) {
      // Scrolling away from the bottom opts out of following until the user comes
      // back down — or opens a chat or sends again. Our own snap lands at the
      // bottom, so it leaves this true.
      stickRef.current = isNearBottom(el);
      if (!stickRef.current) snapRef.current = false;
    }
    void maybeMarkRead();
  };

  const createChat = useMutation({
    mutationFn: async () => {
      const body: any = {
        topicId: topicId ? Number(topicId) : undefined,
        topicTitle: topicId ? undefined : topicTitle.trim() || undefined,
        initialText: firstMessage.trim() || undefined,
      };
      if (!user) {
        body.guestName = guestName.trim();
        body.guestEmail = guestEmail.trim();
        body.guestPhone = guestPhone.trim();
      }
      const res = await fetch("/api/support/widget/chats", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed to create chat");
      return payload as any;
    },
    onSuccess: async (data: any) => {
      const chatId = Number(data?.chatId || 0) || 0;
      if (!user) {
        try {
          localStorage.setItem(
            "fx_support_guest_profile",
            JSON.stringify({ name: guestName.trim(), email: guestEmail.trim(), phone: guestPhone.trim() }),
          );
        } catch {
          // ignore
        }
      }
      setFirstMessage("");
      await qc.invalidateQueries({ queryKey: ["/api/support/widget/chats", deviceId] });
      if (chatId) {
        setActiveChatId(chatId);
        setView("chat");
        await qc.invalidateQueries({ queryKey: messagesKey(chatId) });
      } else {
        setView("home");
      }
    },
  });

  const sendMessage = useMutation({
    mutationFn: async (vars: { chatId: number; tempId: number; text: string }) => {
      const res = await fetch(`/api/support/widget/chats/${vars.chatId}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ text: vars.text.trim() }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((payload as any)?.message || "Failed");
      return { payload, vars };
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: messagesKey(vars.chatId) });
      const nowIso = new Date().toISOString();
      const optimistic: WidgetMessage = {
        id: vars.tempId,
        chatId: vars.chatId,
        senderType: "user",
        text: vars.text.trim(),
        attachments: [],
        createdAt: nowIso,
        _clientStatus: "pending",
      };
      qc.setQueryData<WidgetMessagesResponse | undefined>(messagesKey(vars.chatId), (old) => {
        if (!old) return old;
        return { ...old, items: [...(old.items || []), optimistic] };
      });
      return { nowIso };
    },
    onError: (_e, vars) => {
      qc.setQueryData<WidgetMessagesResponse | undefined>(messagesKey(vars.chatId), (old) => {
        if (!old) return old;
        const items = (old.items || []).map((m) =>
          Number(m.id) === Number(vars.tempId) ? ({ ...m, _clientStatus: "failed" as const } as WidgetMessage) : m,
        );
        return { ...old, items };
      });
    },
    onSuccess: async ({ payload, vars }) => {
      const id = Number((payload as any)?.messageId || 0) || 0;
      if (!id) return;
      qc.setQueryData<WidgetMessagesResponse | undefined>(messagesKey(vars.chatId), (old) => {
        if (!old) return old;
        const items = (old.items || []).map((m) =>
          Number(m.id) === Number(vars.tempId) ? ({ ...m, id, _clientStatus: undefined } as WidgetMessage) : m,
        );
        return { ...old, items };
      });
      await qc.invalidateQueries({ queryKey: ["/api/support/widget/chats", deviceId] });
    },
  });

  const handleSend = () => {
    const chatId = Number(activeChatId || 0);
    if (!chatId) return;
    const text = draft.trim();
    if (!text) return;
    const tempId = -1 * (Date.now() + Math.floor(Math.random() * 10_000));
    setDraft("");

    // The message the user just wrote is the one they want to be looking at.
    stickRef.current = true;
    snapRef.current = true;

    sendMessage.mutate({ chatId, tempId, text });

    // Tapping Send moves focus to the button, and a text field losing focus is what
    // dismisses the soft keyboard — so sending closed the keyboard and the next
    // message began by re-opening it. Put the caret back. `preventScroll` keeps the
    // browser from scrolling the page to reveal the textarea, which would shift the
    // panel at the exact moment the keyboard is animating.
    requestAnimationFrame(() => composerRef.current?.focus({ preventScroll: true }));
  };

  /**
   * The panel has to fit between the sticky navbar band and the bottom of the visible
   * area — which is the top edge of the keyboard while it is open.
   *
   * `dvh` is not enough on its own: Android Chrome keeps the LAYOUT viewport at full
   * height while the keyboard is up (`interactive-widget=resizes-visual` is the
   * default), so a `dvh`-capped panel keeps its full height and its top half ends up
   * off-screen. The visual viewport is the area actually visible, keyboard included.
   *
   * On small screens the navbar band is subtracted as well, which is what keeps the
   * header row — and the minimize button in it — clear of the navbar instead of under
   * it. `navBand` is measured from the visible top, so it reads 0 once the sticky band
   * has been scrolled out of sight.
   */
  const navBand = useMemo(
    () => (isDesktop ? 0 : navBandHeight(viewport.offsetTop)),
    [isDesktop, viewport.offsetTop, viewport.height, viewport.width],
  );

  /** 12px of air above the panel and 12px below it, inside the visible box. */
  const panelMaxHeight = Math.max(240, Math.round(viewport.height - navBand - 24));

  if (hidden) return null;

  const openWidget = (nextView: "home" | "new" | "chat" = "home") => {
    allowSound();
    setOpen(true);
    setView(nextView);
  };

  const closeWidget = () => {
    setOpen(false);
  };

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ view?: "home" | "new" | "chat" }>).detail;
      const nextView = detail?.view === "new" || detail?.view === "chat" ? detail.view : "home";
      allowSound();
      setOpen(true);
      setView(nextView);
    };

    window.addEventListener("fx:support-chat-open", onOpen as EventListener);
    return () => window.removeEventListener("fx:support-chat-open", onOpen as EventListener);
  }, []);

  return (
    /**
     * The visible area, expressed in the visual viewport's own coordinates.
     *
     * `position: fixed` is anchored to the LAYOUT viewport, so `bottom: 12px` means
     * "12px above the bottom of the layout viewport" — which is behind the keyboard,
     * and measured against a height Chrome does not keep in step with its own browser
     * bars. Lifting the panel by a computed keyboard height was the previous approach,
     * and it left a dead band between the composer and the keyboard:
     * `window.innerHeight - visualViewport.height` overstates the keyboard by about the
     * height of the address bar on a phone.
     *
     * Sizing a fixed box from the visual viewport removes that arithmetic entirely —
     * `top` + `height` reproduce exactly what is on screen, so a child pinned to the
     * box's bottom edge lands on the top edge of the keyboard, open or closed, and
     * nothing has to move when the keyboard opens or closes.
     *
     * `pointer-events-none` because the box covers the whole visible area: everything
     * outside the widget has to stay clickable.
     */
    <div
      className="fx-support-chat-widget pointer-events-none fixed z-[60]"
      style={{
        top: `${viewport.offsetTop}px`,
        left: `${viewport.offsetLeft}px`,
        width: `${viewport.width}px`,
        height: `${viewport.height}px`,
      }}
    >
      <div className="pointer-events-auto absolute bottom-3 left-4" onPointerDown={allowSound}>
      <AnimatePresence initial={false} mode="wait">
      {open ? (
        <motion.div
          key="support-chat-panel"
          initial={{ opacity: 0, y: 24, scale: 0.92, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: 18, scale: 0.96, filter: "blur(8px)" }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          // `max-h` is what keeps the panel on screen. Without it the chat view's fixed
          // 520px body plus the two headers could total more than the visible height, and
          // because the panel is anchored to `bottom-4` the overflow went off the TOP —
          // the widget's own title was clipped under the browser chrome. The cap is
          // measured from the visual viewport (see `panelMaxHeight`) so it also holds
          // while the keyboard is up; the class is the CSS-only fallback.
          className="flex max-h-[calc(100dvh-2rem)] w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[28px] border border-white/10 bg-[#0a101f]/95 shadow-[0_24px_90px_rgba(0,0,0,0.75)] backdrop-blur-xl transition-[max-height] duration-200 ease-out"
          style={{ maxHeight: `${panelMaxHeight}px` }}
        >
          {/* The chat view renders its own single-row header — back, agent, minimize,
              close — so this title row is skipped while a conversation is on screen.
              Two stacked headers were eating a third of the panel on a phone. */}
          {view === "chat" && activeChatId ? null : (
          <div className="shrink-0 flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-500">Live Chat</div>
              <div className="text-sm font-black truncate">Support</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors hover:bg-white/10"
                onClick={closeWidget}
                aria-label="Minimize"
              >
                <Minus className="w-4 h-4" />
              </button>
                <button
                  type="button"
                  className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors hover:bg-white/10"
                  onClick={() => {
                    closeWidget();
                    setView("home");
                    // keep last active chat for quick return
                  }}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
            </div>
          </div>
          )}

          {view === "home" ? (
            // `min-h-0` (not `flex-1`, which would collapse an auto-height panel) lets
            // this row shrink inside the panel's cap, and `overflow-y-auto` turns the
            // squeeze into a scroll rather than into unreachable clipped content.
            <div className="min-h-0 overflow-y-auto fx-scroll p-4">
              <button
                type="button"
                className="w-full rounded-3xl border border-white/10 bg-white/5 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/10"
                onClick={() => setView("new")}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 text-left">
                    <div className="font-black">New conversation</div>
                    <div className="text-xs text-gray-500 mt-1">We typically reply in a few minutes</div>
                  </div>
                  <div className="h-10 w-10 rounded-2xl bg-primary text-black grid place-items-center">
                    <Send className="w-4 h-4" />
                  </div>
                </div>
              </button>

              <div className="mt-4">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-500">Recent</div>
                <div className="mt-2 space-y-2">
                  {chatsQuery.isLoading ? (
                    <div className="text-sm text-gray-400 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  ) : chats.length ? (
                    chats.slice(0, 6).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full rounded-3xl border border-white/10 bg-[#070b14]/35 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/5"
                        onClick={() => {
                          setActiveChatId(c.id);
                          setView("chat");
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="font-black truncate">{c.topicTitle || "Support"}</div>
                              {String(c.status || "open").toLowerCase() === "closed" ? (
                                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-gray-300">
                                  Closed
                                </span>
                              ) : null}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 truncate">{c.lastMessagePreview || "—"}</div>
                          </div>
                          <div className="shrink-0 flex flex-col items-end gap-2">
                            <div className="text-[11px] text-gray-500">{fmtTime(c.lastMessageAt || null)}</div>
                            {Number(c.unreadCount || 0) > 0 ? (
                              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-black text-black">
                                {Number(c.unreadCount || 0) > 99 ? "99+" : String(c.unreadCount)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-5 text-sm text-gray-400">
                      No chats yet.
                    </div>
                  )}
                </div>
              </div>

              {/* Moved out of the chat header, where it was competing with the agent's
                  name for the same 328px. From here it also works before any chat has
                  been opened. */}
              <button
                type="button"
                className="mt-4 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-black text-gray-300 transition-colors hover:bg-white/10"
                onClick={openInMessages}
              >
                Open the full messages inbox
              </button>
            </div>
          ) : null}

          {view === "new" ? (
            <div className="min-h-0 overflow-y-auto fx-scroll p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-black">Start a new chat</div>
                <button type="button" className="text-xs text-gray-400 hover:text-white" onClick={() => setView("home")}>
                  Back
                </button>
              </div>

              <div className="space-y-2">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-500">Topic</div>
                {topicOptions.length > 0 ? (
                  <Select value={topicId} onValueChange={(v) => {
                    setTopicId(v);
                    setTopicTitle("");
                  }}>
                    <SelectTrigger className="bg-[#070b14] border-white/10 text-white rounded-2xl h-11">
                      <SelectValue placeholder="Choose a topic" />
                    </SelectTrigger>
                    {/* No forced `side="top"`. On a phone the panel sits near the
                        bottom of the screen, so Radix opened the list upwards and it
                        landed across the "Topic" label above the trigger. Letting
                        Radix choose, with collision detection, keeps it clear. */}
                    <SelectContent align="start" className="bg-[#0a101f] border-white/10 text-white z-[120]">
                      {topicOptions.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)} className="focus:bg-white/10 focus:text-white">
                          {t.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2.5 text-xs leading-5 text-gray-400">
                    {topicsQuery.isLoading
                      ? "Loading topics…"
                      : "No preset topics yet — type your topic in the box below."}
                  </div>
                )}
                {!topicId ? (
                  <Input
                    value={topicTitle}
                    onChange={(e) => setTopicTitle(e.target.value)}
                    className="bg-[#070b14] border-white/10 text-white rounded-2xl h-11"
                    placeholder="Or type a topic"
                  />
                ) : null}
              </div>

              {!user ? (
                <div className="grid grid-cols-1 gap-2">
                  <Input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="bg-[#070b14] border-white/10 text-white rounded-2xl h-11"
                    placeholder="Your name"
                  />
                  <Input
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    className="bg-[#070b14] border-white/10 text-white rounded-2xl h-11"
                    placeholder="Email"
                  />
                  <Input
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="bg-[#070b14] border-white/10 text-white rounded-2xl h-11"
                    placeholder="Phone"
                  />
                </div>
              ) : null}

              <Textarea
                value={firstMessage}
                onChange={(e) => setFirstMessage(e.target.value)}
                className="bg-[#070b14] border-white/10 text-white rounded-3xl min-h-[100px]"
                placeholder="Write your first message…"
              />

              <Button
                className="w-full bg-primary text-black font-black rounded-2xl"
                disabled={createChat.isPending || (!topicId && topicTitle.trim().length < 2) || (!user && (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim())) || firstMessage.trim().length < 1}
                onClick={() => createChat.mutate()}
              >
                {createChat.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Start chat
              </Button>
            </div>
          ) : null}

          {view === "chat" && activeChatId ? (
            // 520px on a desktop panel; on a phone it yields to whatever is actually
            // visible so the panel still fits above the bottom edge.
            <div className="flex min-h-0 flex-col h-[520px] max-h-[calc(100dvh-7rem)]">
              {/* One compact row: back, agent, minimize, close.
                  The identity used to be three stacked lines with two text actions on a
                  second row — around 100px of a panel that is only ~430px tall on a
                  phone with the keyboard up, which is what left the transcript as a
                  sliver. The actions are icon buttons now, and "Open in Messages" moved
                  to the chat list, where it costs the header no space at all.
                  Every control is 36px, comfortably over the 24px minimum target. */}
              <div className="flex shrink-0 items-center gap-2 border-b border-white/10 px-2.5 py-2">
                <button
                  type="button"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors hover:bg-white/10"
                  onClick={() => setView("home")}
                  aria-label="Back to chat list"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full border border-white/10 bg-white/5">
                  {assignedAgent?.profileImageUrl ? (
                    <img src={assignedAgent.profileImageUrl} alt={assignedAgent.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[11px] font-black text-gray-200">{initialsFromName(assignedAgent?.name || "Support")}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-black leading-tight">{assignedAgent?.name || "Support"}</div>
                  <div className="truncate text-[11px] leading-tight text-gray-500">
                    Topic: {chatMeta?.topicTitle || "Support"}
                  </div>
                </div>
                <button
                  type="button"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors hover:bg-white/10"
                  onClick={closeWidget}
                  aria-label="Minimize"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-colors hover:bg-white/10"
                  onClick={() => {
                    closeWidget();
                    setView("home");
                    // keep last active chat for quick return
                  }}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div
                ref={scrollerRef}
                // `overscroll-contain` removed to match the dashboard messenger: it
                // blocked the gesture from chaining out, so a drag over the transcript
                // did nothing once the list had no scroll left.
                className="flex-1 min-h-0 fx-scroll overflow-y-auto overscroll-x-contain p-3 sm:p-4"
                onScroll={onTranscriptScroll}
              >
                {/* One wrapper so a single ResizeObserver can watch the whole transcript
                    grow. That is what keeps the newest message in view when the layout
                    settles after the first paint. */}
                <div ref={transcriptRef} className="space-y-3">
                {messagesQuery.isLoading ? (
                  <div className="text-sm text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : messagesQuery.isError ? (
                  <div className="text-sm text-red-300">{(messagesQuery.error as any)?.message || "Failed"}</div>
                ) : messages.length ? (
                  messages.map((m) => {
                    const mine = m.senderType === "user";
                    return (
                      <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                        {!mine ? (
                          <div className="mr-2 mt-1 w-9 h-9 rounded-full border border-white/10 bg-white/5 overflow-hidden grid place-items-center shrink-0">
                            {assignedAgent?.profileImageUrl ? (
                              <img src={assignedAgent.profileImageUrl} alt={assignedAgent.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] font-black text-gray-200">{initialsFromName(assignedAgent?.name || "Support")}</span>
                            )}
                          </div>
                        ) : null}
                        <div className={cn("max-w-[78%]", mine ? "items-end" : "items-start")}
                        >
                          <div
                            className={cn(
                              "rounded-3xl border px-4 py-3",
                              mine ? "bg-primary text-black border-primary/30" : "bg-white/5 text-white border-white/10",
                            )}
                          >
                            <div className={cn("text-sm whitespace-pre-wrap", mine ? "text-black" : "text-white")}>{m.text}</div>
                          </div>
                          <div className={cn("mt-1 text-[11px] text-gray-500", mine ? "text-right" : "text-left")}>
                            {fmtTime(m.createdAt || null)}
                            {mine && m._clientStatus === "pending" ? (
                              <span className="ml-2 inline-flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending
                              </span>
                            ) : null}
                            {mine && m._clientStatus === "failed" ? <span className="ml-2 text-red-300">Failed</span> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-sm text-gray-400">No messages yet.</div>
                )}
                </div>
              </div>

              <div className="shrink-0 border-t border-white/10 bg-[#070b14]/35 p-3 sm:p-4">
                {String(chatMeta?.status || "open").toLowerCase() === "closed" ? (
                  <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-gray-200">
                    <div className="font-black">This chat is closed</div>
                    <div className="text-xs text-gray-500 mt-1">Start a new conversation if you still need help.</div>
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        type="button"
                        className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-black text-gray-200 hover:bg-white/10"
                        onClick={() => setView("home")}
                      >
                        Back to messages
                      </button>
                      <button
                        type="button"
                        className="flex-1 rounded-2xl border border-primary/25 bg-primary/10 px-3 py-2 text-xs font-black text-primary hover:bg-primary/15"
                        onClick={() => setView("new")}
                      >
                        New conversation
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end gap-2">
                      <Textarea
                        ref={composerRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        rows={1}
                        className="flex-1 bg-[#070b14] border-white/10 text-white rounded-3xl min-h-[44px] max-h-[120px] resize-none px-4 py-3"
                        placeholder="Type a message…"
                      />
                      <Button
                        className="bg-primary text-black font-black rounded-2xl h-11"
                        disabled={!draft.trim()}
                        // Focus must not leave the textarea. A text field losing focus is
                        // what dismisses the soft keyboard, so tapping Send closed it and
                        // the next message had to start by re-opening it.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSend}
                      >
                        <Send className="w-4 h-4 mr-2" /> Send
                      </Button>
                    </div>
                    {/* Keyboard hint only where there is a keyboard to hint at; on a
                        phone it is a third of the composer's height for no benefit. */}
                    {isDesktop ? (
                      <div className="mt-2 text-[11px] text-gray-500">Enter to send. Shift+Enter for a new line.</div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ) : null}
        </motion.div>
      ) : (
        <motion.button
          key="support-chat-fab"
          type="button"
          initial={{ opacity: 0, y: 18, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.9 }}
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -4, scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          className="relative grid h-14 w-14 place-items-center rounded-full border border-white/10 bg-[#0a101f]/90 shadow-[0_18px_60px_rgba(0,0,0,0.65)] backdrop-blur-xl"
          onClick={() => openWidget()}
          aria-label="Open live chat"
        >
          <span className="absolute inset-0 rounded-full bg-primary/15 blur-xl" />
          <span className="absolute inset-[1px] rounded-full border border-primary/15" />
          <MessageSquare className="relative z-10 w-6 h-6 text-primary" />
          {unreadTotal > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-black text-white shadow-[0_10px_24px_rgba(239,68,68,0.45)]">
              {unreadTotal > 99 ? "99+" : String(unreadTotal)}
            </span>
          ) : null}
        </motion.button>
      )}
      </AnimatePresence>
      </div>
    </div>
  );
}
